import React from 'react';
import { StyleSheet, View } from 'react-native';
import { Checkbox, Chip, Text, TouchableRipple } from 'react-native-paper';
import type {
  ReviewFinding,
  ReviewFindingActionability,
  ReviewFindingSeverity,
} from '../types';
import { colors, spacing, typography } from '../constants/theme';

interface FindingEditorProps {
  questionId: string | null;
  finding?: ReviewFinding;
  label?: string;
  onChange: (finding: ReviewFinding | null) => void;
}

const severityOptions: Array<{ value: ReviewFindingSeverity; label: string }> = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const actionabilityOptions: Array<{ value: ReviewFindingActionability; label: string }> = [
  { value: 'monitor', label: 'Monitor' },
  { value: 'planned', label: 'Planned' },
  { value: 'near_term', label: 'Near term' },
  { value: 'immediate', label: 'Immediate' },
];

export const FindingEditor: React.FC<FindingEditorProps> = ({
  questionId,
  finding,
  label = 'Flag as a finding',
  onChange,
}) => {
  const update = (updates: Partial<ReviewFinding>) => {
    if (!finding) return;
    onChange({ ...finding, ...updates, questionId });
  };

  const toggle = () => {
    if (finding) {
      onChange(null);
      return;
    }

    const localId = `finding_${questionId || 'item'}_${Date.now()}`;
    onChange({
      id: -Date.now(),
      reviewResponseId: 0,
      questionId,
      severity: null,
      actionability: null,
      clientId: localId,
      tempId: localId,
    });
  };

  return (
    <View testID={`finding-editor-${questionId || 'item'}`} style={styles.container}>
      <TouchableRipple onPress={toggle}>
        <View style={styles.toggleRow}>
          <Checkbox
            testID={`finding-toggle-${questionId || 'item'}`}
            status={finding ? 'checked' : 'unchecked'}
            onPress={toggle}
          />
          <Text style={styles.label}>{label}</Text>
        </View>
      </TouchableRipple>

      {finding && (
        <View style={styles.ratings}>
          <Text style={styles.ratingLabel}>Severity (optional)</Text>
          <View style={styles.chips}>
            {severityOptions.map(option => (
              <Chip
                key={option.value}
                testID={`finding-severity-${questionId || 'item'}-${option.value}`}
                selected={finding.severity === option.value}
                onPress={() => update({
                  severity: finding.severity === option.value ? null : option.value,
                })}
                style={styles.chip}
              >
                {option.label}
              </Chip>
            ))}
          </View>

          <Text style={styles.ratingLabel}>Actionability (optional)</Text>
          <View style={styles.chips}>
            {actionabilityOptions.map(option => (
              <Chip
                key={option.value}
                testID={`finding-actionability-${questionId || 'item'}-${option.value}`}
                selected={finding.actionability === option.value}
                onPress={() => update({
                  actionability: finding.actionability === option.value ? null : option.value,
                })}
                style={styles.chip}
              >
                {option.label}
              </Chip>
            ))}
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: spacing.sm,
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    backgroundColor: colors.surface,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  label: {
    ...typography.body2,
    color: colors.text,
    fontWeight: '600',
  },
  ratings: {
    marginTop: spacing.xs,
  },
  ratingLabel: {
    ...typography.caption,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  chip: {
    marginBottom: spacing.xs,
  },
});

export default FindingEditor;
